import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useFetchBillDetailsByMatterLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY } from '../constants'
import { AuthContext } from '../context'
import { BillDetail } from '../interfaceTypes/common'
import useFetchBillInterest from './useFetchBillInterest'
import { calculateClientBillData } from '../utils/billing'
import useFetchOutstandingBills from './useFetchOutstandingBills'

interface UseFetchBillDetailsByMatterProps {
  matterId: string | undefined
  clientId: string | undefined
  includeInterest: boolean
}

const useFetchBillDetailsByMatter = ({ matterId, clientId, includeInterest }: UseFetchBillDetailsByMatterProps) => {
  const [bill, setBill] = useState<BillDetail>()
  const { currentUser } = useContext(AuthContext)

  const selectedClientId = clientId

  const selectedAccessToken = useMemo(
    () => bill?.accessTokens?.find(token => token.client?.id === selectedClientId),
    [bill?.accessTokens, selectedClientId]
  )

  const interestData = useFetchBillInterest({
    billId: bill?.id,
    billType: bill?.type,
    clientId: selectedClientId,
    token: selectedAccessToken?.token
  })

  const outstandingBillsData = useFetchOutstandingBills({
    caseId: bill?.case?.id,
    billId: bill?.id,
    billStatus: bill?.status,
    billType: bill?.type,
    clientId: selectedClientId,
    token: selectedAccessToken?.token,
    isPrebillView: false
  })

  const [fetchBillDetailsByMatter, { refetch, loading, error }] = useFetchBillDetailsByMatterLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError() {
      // Bill not found for this matter - clear the bill
      setBill(undefined)
    },

    onCompleted(data) {
      if (data?.fetchBillDetailsByMatter) {
        const { bill, response } = data.fetchBillDetailsByMatter
        const { status } = response || {}

        if (status === 200 && bill) {
          setBill(bill)
        } else {
          setBill(undefined)
        }
      }
    }
  })

  const clientData = useMemo(() => {
    const clientWrittenOff =
      (selectedAccessToken?.writtenOffPrincipal || 0) + (selectedAccessToken?.writtenOffInterest || 0)
    const billWrittenOff = (bill?.writtenOffPrincipal || 0) + (bill?.writtenOffInterest || 0)

    return calculateClientBillData({
      paymentAllocations: bill?.paymentAllocations || [],
      showPrebillView: false,
      previousBalance: outstandingBillsData?.previousBalance,
      bill: {
        amount: bill?.amount,
        splitBill: !!bill?.case?.billing?.splitBillEnabled,
        status: bill?.status,
        discount: bill?.discount,
        interestAccrued: includeInterest ? interestData.interestAccrued : undefined,
        writtenOff: billWrittenOff
      },
      client: { id: selectedClientId, share: selectedAccessToken?.amount, writtenOff: clientWrittenOff }
    })
  }, [
    bill,
    selectedClientId,
    selectedAccessToken?.amount,
    selectedAccessToken?.writtenOffPrincipal,
    selectedAccessToken?.writtenOffInterest,
    includeInterest,
    interestData.interestAccrued,
    outstandingBillsData?.previousBalance
  ])

  // Use refs so refetchData is stable and doesn't cause cascading re-renders
  const interestRefetchRef = useRef(interestData.refetch)
  interestRefetchRef.current = interestData.refetch
  const outstandingRefetchRef = useRef(outstandingBillsData.refetch)
  outstandingRefetchRef.current = outstandingBillsData.refetch

  const refetchData = useCallback(() => {
    refetch()
    interestRefetchRef.current()
    outstandingRefetchRef.current()
  }, [refetch])

  useEffect(() => {
    if (matterId && currentUser) {
      fetchBillDetailsByMatter({
        variables: {
          fetchBillDetailsByMatterInput: {
            matterId
          }
        }
      })
    } else {
      setBill(undefined)
    }
  }, [matterId, currentUser, clientId, fetchBillDetailsByMatter])

  return {
    bill,
    clientData,
    interestData,
    outstandingBillsData,
    refetch: refetchData,
    loading,
    error
  }
}

export default useFetchBillDetailsByMatter
